import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Divider,
} from "@chakra-ui/react";
import { MdEvStation, MdLocationOn, MdPhone, MdEmail } from "react-icons/md";
import useAxios from "axios-hooks";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "components/feedback/LoadingSpinner";

// Charger Info Card Component
const ChargerInfoCard = ({ station, cardBg, borderColor, labelColor }) => {
  const [connectors, setConnectors] = useState([]);
  const navigate = useNavigate();
  
  const [{ data }, getConnectors] = useAxios(
    { method: "GET", url: `/api/evses/forStation/${station.Id}` },
    { manual: true }
  );

  useEffect(() => {
    const fetchConnectors = async () => {
      try {
        const response = await getConnectors();
        setConnectors(response.data.data || []);
      } catch (error) {
        console.error("Error fetching connectors:", error);
      }
    };
    
    if (station.Id) {
      fetchConnectors();
    }
  }, [station.Id]);

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      p={{ base: 3, md: 6 }}
      boxShadow="sm"
      transition="all 0.3s ease"
      cursor="pointer"
      onClick={() => navigate(`/admin/charging_management/charger/${station.Id}`)}
      _hover={{
        boxShadow: "md",
        transform: "translateY(-2px)",
      }}
    >
      <VStack align="stretch" spacing={{ base: 2, md: 4 }}>
        <Flex align="center" gap={{ base: 1.5, md: 2 }}>
          <Icon as={MdEvStation} color="brand.500" boxSize={{ base: 4, md: 5 }} />
          <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold">{station.StationId || `TEST00${station.Id}`}</Text>
        </Flex>
        
        <Divider />
        
        <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
          <HStack justify="space-between">
            <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor} fontWeight="semibold">CODE</Text>
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">{station.StationId || `TEST00${station.Id}`}</Text>
          </HStack>
          
          <HStack justify="space-between">
            <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor} fontWeight="semibold">POWER</Text>
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">{station.MaxElectricPower || "50"} kWh</Text>
          </HStack>
          
          <HStack justify="space-between">
            <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor} fontWeight="semibold">TYPE</Text>
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">{station.PowerType || "DC"}</Text>
          </HStack>
          
          <HStack justify="space-between">
            <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor} fontWeight="semibold">PRICE</Text>
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium" color="brand.500">
              {station.Tariffs && station.Tariffs.length > 0 && station.Tariffs[0].Price 
                ? `₹${station.Tariffs[0].Price}/unit` 
                : "N/A"}
            </Text>
          </HStack>
          
          <HStack justify="space-between">
            <Text fontSize={{ base: "xs", md: "sm" }} color={labelColor} fontWeight="semibold">NO. OF CONNECTORS</Text>
            <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="medium">{connectors.length || "0"}</Text>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};

const ChargingManagementPanel = ({ locationId, locationName, locationData }) => {
  const [stations, setStations] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const navigate = useNavigate();

  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  const [{ data, loading }, getStations] = useAxios(
    { method: "GET", url: `/api/stations/forLocation/${locationId}` },
    { manual: true }
  );

  const fetchStations = async () => {
    try {
      const response = await getStations();
      setStations(response.data.data || []);
    } catch (error) {
      console.error("Error fetching stations:", error);
      setStations([]);
    }
  };

  useEffect(() => {
    if (locationId) {
      fetchStations();

      // const pollInterval = setInterval(() => {
      //   if (isInitialLoad) {
      //     setIsInitialLoad(false);
      //   }
      //   fetchStations();
      // }, 10000);

      // return () => clearInterval(pollInterval);
    }
  }, [locationId]);

  if (loading && isInitialLoad) {
    return (
      <Box pt={{ base: 40, md: 10 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (stations.length === 0) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        minH="400px"
        bg={bgColor}
        borderRadius="xl"
        p={8}
      >
        <Icon as={MdEvStation} boxSize={16} color="brand.500" mb={4} />
        <Text fontSize="xl" fontWeight="bold" mb={2}>
          No Stations Found
        </Text>
        <Text color="gray.500" textAlign="center" maxW="md">
          There are no charging stations available for this location.
        </Text>
      </Flex>
    );
  }

  return (
    <Box bg={bgColor} pt={{ base: 5, md: 5 }} px={{ base: 2, md: 4 }}>
      {/* Chargers Grid */}
      <SimpleGrid
        columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
        spacing={{ base: 4, md: 6 }}
      >
        {stations.map((station, index) => (
          <ChargerInfoCard
            key={station.Id || index}
            station={station}
            cardBg={cardBg}
            borderColor={borderColor}
            labelColor={labelColor}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default ChargingManagementPanel;
